/**
 * Период аналитики: от главного прихода до следующего.
 *
 * Календарный месяц плохо описывает жизнь человека с зарплатой: октябрь у
 * него — половина одного периода и половина следующего, и вопрос «хватило ли
 * дохода» на месяц не отвечается. Поэтому картина денег считается от прихода
 * до прихода, а месяц остаётся честным запасным вариантом.
 *
 * Это **не горизонт главной**. Горизонт (`resolveHorizon`) отвечает, до какого
 * дня дожить сегодняшним деньгам, и для него аванс — такая же точка, как
 * зарплата. Период отвечает, на что ушёл один доход, и аванс его не режет.
 * Эти расчёты не смешиваются, и горизонт здесь не трогается.
 *
 * Правила модуля:
 *
 * · **Чью зарплату закрывает приход, решается один раз** — когда приход
 *   записан — и хранится в самой операции (`occurrence`). Приход, закрывший
 *   ожидание («зарплата 5 ноября — пришла»), берёт его дату: это явная
 *   связь. Приход без связи относится к последней дате по расписанию, до
 *   которой осталось не больше трёх дней, — то же правило, по которому
 *   приход закрывает наступление на главной. Поэтому поздняя доплата
 *   остаётся частью своей зарплаты, а не открывает следующий период.
 *   Правка расписания и удаление дохода прошлое не переписывают.
 * · **Границы выводятся из операций.** Исправил дату зарплаты или удалил
 *   операцию — периоды пересчитались, и каждая операция по-прежнему попадает
 *   ровно в один период.
 * · **Хранится только выбор источника** (`PeriodPin`): какой доход главный и
 *   с какого дня. Новый источник вступает с первого своего прихода и идущий
 *   период не перекраивает.
 * · **Главный приход не выдумывается.** Срок задаёт больше одного дохода,
 *   аванс и зарплата записаны одним доходом с двумя числами, доход приходит
 *   каждую неделю или когда как — период считается календарным месяцем.
 */
import { ZERO, add, type Money } from './money.js';
import {
  addDays, daysInMonth, isAfter, isBefore, parts, ymd, type PlainDate,
} from './date.js';
import { conservativeIncome, nextByRhythm, previousOccurrence, rhythmOf, roleOfStream } from './income.js';
import {
  activeStablePlan, decisionOf, isPaused, paidOnOf, payoutOccurrences,
  type StableBudgetPlan, type StablePayoutFact,
} from './stable.js';
import type { IncomeExpectation, IncomeStream } from './types.js';
import type { PlanLineKind, PlanPeriod } from './budget.js';
import type { PlanMode } from './plan.js';

export type PeriodKind = 'salary' | 'stable' | 'month';

/** Что задаёт периоды: зарплата, выплата себе или календарь. */
export interface PeriodSource {
  kind: PeriodKind;
  /** Источник дохода или план стабильного бюджета. У месяца нет. */
  sourceId?: string;
}

/**
 * Выбор источника, закреплённый с какого-то дня. Хранится.
 *
 * Не граница периода: граница — первый приход этого источника начиная с
 * `since`. До него продолжается период прежнего источника.
 */
export interface PeriodPin extends PeriodSource {
  since: PlainDate;
  /**
   * Когда закрепление записано. Приход нового источника, внесённый после
   * этого задним числом, — тоже его приход (`reportPeriods`). У старых
   * закреплений поля нет — тогда сравнивается день записи прихода.
   */
  at?: string;
  /**
   * Только у первого закрепления: начало первого периода, если оно взято из
   * расписания (зарплата пришла до начала учёта).
   *
   * Закрепляется при создании: перенос зарплатного дня потом не должен
   * сдвигать начало истории.
   */
  startedFrom?: PlainDate;
}

/**
 * Как известно, какую зарплату закрывает приход.
 *
 * `expectation` — приход закрыл ожидание; `confirmed` — человек уточнил сам;
 * `schedule` — оценка по расписанию: правдоподобно, но не доказано, и
 * интерфейс вправе переспросить именно про эту операцию.
 */
export type OccurrenceBasis = 'expectation' | 'confirmed' | 'schedule';

/** Приход — ровно то, что нужно для границ. */
export interface PeriodIncome {
  /** Операция: по ней период узнаёт себя после правки даты. */
  txId: string;
  date: PlainDate;
  amount: Money;
  streamId?: string;
  kind: 'regular' | 'oneoff' | 'refund';
  /** Записанное в операции наступление дохода, которое закрывает приход. */
  occurrence?: PlainDate;
  occurrenceBasis?: OccurrenceBasis;
  /** Дата ожидания, которое закрыл приход, — пока наступление не записано. */
  expectedDate?: PlainDate;
  /**
   * Сколько человек ещё ждёт по этой зарплате после этого прихода — его
   * ответ при записи части («жду остаток» / «остаток не жду»).
   *
   * Ноль — «больше не жду», и это ответ. Нет поля — ответа не было.
   */
  expectedRest?: Money;
  /**
   * Когда операция записана. По этому времени, а не по дате прихода и не по
   * идентификатору, упорядочены части зарплаты и ответы человека: две части
   * в один день или часть, внесённая задним числом, иначе встали бы в
   * случайном порядке.
   */
  recordedAt?: string;
}

/**
 * Откуда считать период — выбор человека (02.11.2026). Без него источник
 * выбирается сам (`mainSource`), и при нескольких доходах это месяц.
 */
export type PeriodChoice = { kind: 'month' } | { kind: 'salary'; sourceId: string };

export interface PeriodInput {
  today: PlainDate;
  /** Выбор человека: с 1-го числа или от прихода дохода. */
  choice?: PeriodChoice | null;
  /** Начало учёта. Раньше него приходы не спрашиваются. */
  since?: PlainDate;
  streams: readonly IncomeStream[];
  incomes: readonly PeriodIncome[];
  stablePlans?: readonly StableBudgetPlan[];
  stablePayouts?: readonly StablePayoutFact[];
  /** Закреплённые источники по возрастанию `since`. */
  pins?: readonly PeriodPin[];
  /** С какого дня нужна история. По умолчанию — начало учёта или год назад. */
  from?: PlainDate;
}

export interface ReportPeriod {
  /**
   * Устойчивый ключ — по тому, что открыло период: наступлению зарплаты,
   * выплате себе или календарному месяцу.
   *
   * Не по дате и не по операции: исправленная дата зарплаты или удалённая
   * первая её часть сдвигают границу, но период остаётся тем же, и его
   * основа сравнения к нему по-прежнему относится.
   */
  key: string;
  kind: PeriodKind;
  sourceId?: string;
  from: PlainDate;
  /** Начало — записанный приход, расписание (приход до начала учёта) или календарь. */
  start: 'fact' | 'schedule' | 'calendar';
  /**
   * Начало подтверждено явно: открывший период приход закрыл ожидание или
   * уточнён человеком; выплата записана; календарь. Судит только открывший
   * приход — сомнительные доплаты внутри периода видны по операциям
   * (`incomeAssignments`), а не этим флагом.
   */
  certain: boolean;
  /** Последний день закрытого периода; `null` — период ещё идёт. */
  to: PlainDate | null;
  /** Ожидаемый последний день идущего периода. `null` — даты нет. */
  expectedTo: PlainDate | null;
  /**
   * Последний день по расписанию — срок плана. У закрытого периода тоже:
   * зарплата, пришедшая с опозданием, закрыла период позже, но план от
   * этого задним числом не удлиняется.
   */
  plannedTo: PlainDate | null;
  /** Наступление зарплаты, которое открыло период. */
  occurrence?: PlainDate;
  /** Выплата себе, которая открыла период. */
  payoutId?: string;
  /** С какого дня есть данные: начало периода или начало учёта. */
  observedFrom: PlainDate;
  /** Учёт начат посреди периода: ранних трат в приложении нет. */
  partial: boolean;
  /** Ожидаемый конец прошёл, а следующего прихода нет. Период тянется. */
  overdue: boolean;
  /**
   * Идущий период кончится не по своему расписанию: источник сменился, и
   * новый вступит с первого своего прихода. `next` — что начнётся дальше;
   * `switchEnd` — последний день по прогнозу, день перед этим приходом.
   * Нет `switchEnd` при `next` — срок неизвестен: у нового источника нет
   * дат. Только у идущего периода и только если смена закончит его раньше
   * своего расписания (или своего расписания больше нет: доход выключен).
   */
  switchEnd?: PlainDate;
  next?: PeriodSource;
  /** Закрытый период кончился сменой источника, а не своим следующим приходом. */
  endedBySwitch?: true;
  why: string;
}

/**
 * Основа сравнения периода: суммы плана, с которыми период начался. Хранится.
 *
 * Одна на период — ключ периода и есть её ключ: две вкладки или два
 * устройства, сохранившие основу одновременно, пишут одну запись.
 */
export interface PeriodBaseline {
  key: string;
  savedAt: string;
  /** Срок плана: от начала периода до ожидаемого конца. */
  term: { from: PlainDate; to: PlainDate };
  lines: Record<PlanLineKind, Money>;
  mode: PlanMode;
  /**
   * Сохранена не в начале периода: приложение открыли посреди него. Такие
   * суммы — сегодняшний снимок, и выдавать их за план на начало нельзя.
   */
  late: boolean;
  /**
   * Срок основы укорочен ожидаемой сменой источника — на какой. Если смена
   * потом другая или отменена, границы основы подтверждённо неверны.
   */
  switchTo?: PeriodSource;
}

export const baselineOf = (
  list: readonly PeriodBaseline[], key: string,
): PeriodBaseline | null => list.find((b) => b.key === key) ?? null;

/**
 * Годится ли сохранённая основа для сравнения с этим периодом.
 *
 * Не годится только при подтверждённой ошибке её границ — из-за смены
 * источника, а не из-за сегодняшнего прогноза:
 *
 * · основа сохранена без смены, а смена случилась и закончит (закончила)
 *   период раньше срока основы: в основе — доходы соседнего периода;
 * · основа рассчитана на смену (`switchTo`), а смена другая или отменена.
 *
 * Обычный перенос зарплатного дня основу не портит: сравнение идёт по её
 * сроку. Сама запись не меняется — это история; отмени смену, и основа,
 * сохранённая без неё, снова подойдёт.
 */
export const baselineFits = (p: ReportPeriod, b: PeriodBaseline): boolean => {
  const switchNow: PeriodSource | null = p.to === null ? p.next ?? null : null;
  if (b.switchTo) {
    if (p.to === null) return !!switchNow && sameSource(switchNow, b.switchTo);
    return !!p.endedBySwitch;
  }
  const end = p.to !== null ? (p.endedBySwitch ? p.to : null) : (p.next ? p.switchEnd ?? null : null);
  return !end || !isAfter(b.term.to, end);
};

// ───────────────────────────────────────────────────── главный источник

/**
 * Какой источник задаёт периоды — по сегодняшним данным.
 *
 * Выбор только однозначный: ровно один доход задаёт срок, и он приходит раз
 * в месяц одним числом. Плавающая сумма этому не мешает — сумма здесь не
 * сравнивается вовсе. Аванс (`advance`) срок для главной задаёт, но период
 * не режет. Подработка, помеченная как пополнение, в выборе не участвует.
 */
export function mainSource(
  input: Pick<PeriodInput, 'streams' | 'stablePlans' | 'choice'>,
): PeriodSource & { why: string } {
  // Стабильный бюджет — выбор человека: он сам назначил себе зарплату.
  const plan = activeStablePlan(input.stablePlans ?? []);
  if (plan) {
    return plan.cadence === 'monthly'
      ? { kind: 'stable', sourceId: plan.id, why: 'От выплаты себе до выплаты' }
      : { kind: 'month', why: 'Выплаты себе чаще раза в месяц — считаю по месяцам' };
  }

  // Выбор человека сильнее догадки: с 1-го числа — месяц, от дохода — его
  // приходы, если доход ещё действует и приходит раз в месяц одним числом.
  const chosen = input.choice;
  if (chosen?.kind === 'month') return { kind: 'month', why: 'Выбрано: с 1-го числа' };
  if (chosen?.kind === 'salary') {
    const s = input.streams.find((x) => x.id === chosen.sourceId && x.isActive);
    const r = s ? rhythmOf(s) : null;
    if (s && r?.kind === 'dates' && r.days.length === 1) {
      return { kind: 'salary', sourceId: s.id, why: `Выбрано: от прихода «${s.name}»` };
    }
  }

  const eligible = input.streams.filter((s) => s.isActive && !s.advance
    && roleOfStream(s) === 'checkpoint' && rhythmOf(s).kind !== 'none');
  if (eligible.length === 0) {
    return { kind: 'month', why: 'Регулярного дохода нет — считаю по месяцам' };
  }
  if (eligible.length > 1) {
    return { kind: 'month', why: 'Срок задают несколько доходов — главный не выбрать, считаю по месяцам' };
  }
  const s = eligible[0]!;
  const r = rhythmOf(s);
  if (r.kind === 'weekly' || r.kind === 'biweekly') {
    return { kind: 'month', why: 'Доход приходит чаще раза в месяц — считаю по месяцам' };
  }
  if (r.kind !== 'dates') {
    return { kind: 'month', why: 'У дохода нет числа — считаю по месяцам' };
  }
  if (r.days.length !== 1) {
    return {
      kind: 'month',
      why: 'Аванс и зарплата записаны одним доходом — их не различить, считаю по месяцам',
    };
  }
  return { kind: 'salary', sourceId: s.id, why: `${s.name}: от прихода до прихода` };
}

/**
 * Зарплата для периода «от зарплаты до зарплаты», когда главный доход не
 * выбран: несколько доходов с датой (зарплата 7-го и подработка 5-го).
 *
 * Самый большой регулярный доход с одним числом в месяц, кроме аванса. Это
 * не главный доход: периоды аналитики по умолчанию остаются месячными, а
 * этот период человек выбирает сам. `null` — главный доход и так зарплата
 * (её периоды — `reportPeriods`) или подходящей зарплаты нет.
 */
export function paydaySource(
  input: Pick<PeriodInput, 'streams' | 'stablePlans'>,
): { sourceId: string; name: string; others: string[] } | null {
  if (mainSource(input).kind !== 'month') return null;
  if (activeStablePlan(input.stablePlans ?? [])) return null;
  const eligible = input.streams.filter((s) => s.isActive && !s.advance
    && roleOfStream(s) === 'checkpoint' && rhythmOf(s).kind !== 'none');
  const monthly = eligible.filter((s) => { const r = rhythmOf(s); return r.kind === 'dates' && r.days.length === 1; });
  const best = [...monthly].sort((a, b) => conservativeIncome(b) - conservativeIncome(a) || a.id.localeCompare(b.id))[0];
  if (!best) return null;
  return { sourceId: best.id, name: best.name, others: eligible.filter((s) => s.id !== best.id).map((s) => s.name) };
}

/**
 * Периоды этой зарплаты — от прихода до прихода, как если бы она была
 * главной: остальные доходы границ не задают, и смены источника нет. Иначе
 * идущий период «заканчивался» бы месяцем — сегодняшним главным выбором.
 */
export function paydayPeriods(input: PeriodInput): ReportPeriod[] {
  const src = paydaySource(input);
  if (!src) return [];
  const streams = input.streams.map((s) => (s.id === src.sourceId ? s : { ...s, advance: true }));
  return reportPeriods({ ...input, streams, pins: [] })
    .filter((p) => p.kind === 'salary' && p.sourceId === src.sourceId);
}

/**
 * Закрепления после явного выбора человека (02.11.2026).
 *
 * Выбор применяется сразу: идущий период пересчитывается от последнего
 * прихода выбранного дохода (или с 1-го числа месяца), а не ждёт следующего
 * прихода — человек, выбравший «от зарплаты», ждёт увидеть свой идущий
 * период с тратами с того дня. Закрепления с этого дня и позже уступают
 * выбору; более ранняя история остаётся.
 */
export function choicePins(pins: readonly PeriodPin[], input: PeriodInput, choice: PeriodChoice): PeriodPin[] {
  let pin: PeriodPin;
  if (choice.kind === 'month') {
    pin = { kind: 'month', since: `${input.today.slice(0, 8)}01` as PlainDate };
  } else {
    const streams = input.streams.map((s) => (s.id === choice.sourceId ? s : { ...s, advance: true }));
    const own = reportPeriods({ ...input, streams, pins: [], choice: null })
      .filter((p) => p.kind === 'salary' && p.sourceId === choice.sourceId && !isAfter(p.from, input.today));
    const cur = own[own.length - 1];
    pin = {
      kind: 'salary', sourceId: choice.sourceId, since: cur?.from ?? input.today,
      ...(cur && cur.start === 'schedule' ? { startedFrom: cur.from } : {}),
    };
  }
  const earlier = pins.filter((p) => isBefore(p.since, pin.since));
  if (earlier.length > 0 && 'startedFrom' in pin) delete pin.startedFrom;
  return [...earlier, pin];
}

const sameSource = (a: PeriodSource, b: PeriodSource): boolean =>
  a.kind === b.kind && (a.sourceId ?? '') === (b.sourceId ?? '');

const sourceOf = (s: PeriodSource): PeriodSource =>
  (s.sourceId ? { kind: s.kind, sourceId: s.sourceId } : { kind: s.kind });

/**
 * Закрепление, которое нужно добавить, — или `null`, если менять нечего.
 *
 * Новый источник закрепляется с сегодняшнего дня, а вступает с первого
 * своего прихода: идущий период он не перекраивает. Первое закрепление
 * запоминает начало истории по расписанию, если зарплата пришла до начала
 * учёта.
 */
export function nextPin(pins: readonly PeriodPin[], input: PeriodInput): PeriodPin | null {
  const fresh = sourceOf(mainSource(input));
  const last = pins[pins.length - 1];
  if (last && sameSource(last, fresh)) return null;
  if (last) return { ...fresh, since: input.today };
  const first = currentPeriod({ ...input, pins: [] });
  return {
    ...fresh,
    since: input.today,
    ...(first.start === 'schedule' && sameSource(first, fresh) ? { startedFrom: first.from } : {}),
  };
}

// ────────────────────────────────────────────── какую зарплату закрывает приход

/**
 * Насколько раньше срока приход ещё закрывает это наступление.
 *
 * То же окно, что у главной (`factClosing`): зарплата, пришедшая на день-два
 * раньше из-за выходных, — та же зарплата. Раньше — уже не угадываем: без
 * явной связи с ожиданием это доплата к прошлой.
 */
export const EARLY_DAYS = 3;

/**
 * Наступление, которое закрывает приход, и откуда это известно.
 *
 * Записанное в операции — сильнее всего: решено, когда деньги пришли.
 * Закрытое ожидание — явная связь. Иначе — последняя дата по расписанию не
 * позже трёх дней после прихода.
 */
export function occurrenceOf(
  stream: IncomeStream | undefined, f: PeriodIncome,
): { occurrence: PlainDate; basis: OccurrenceBasis } | null {
  if (f.occurrence) return { occurrence: f.occurrence, basis: f.occurrenceBasis ?? 'schedule' };
  if (f.expectedDate) return { occurrence: f.expectedDate, basis: 'expectation' };
  if (!stream) return null;
  const occ = previousOccurrence({ ...stream, isActive: true }, addDays(f.date, EARLY_DAYS));
  return occ ? { occurrence: occ, basis: 'schedule' } : null;
}

/** Источники зарплатных периодов: закреплённые и сегодняшний главный. */
function salarySources(input: PeriodInput): Set<string> {
  const ids = new Set<string>();
  for (const p of input.pins ?? []) if (p.kind === 'salary' && p.sourceId) ids.add(p.sourceId);
  const fresh = mainSource(input);
  if (fresh.kind === 'salary' && fresh.sourceId) ids.add(fresh.sourceId);
  return ids;
}

/**
 * Приходы, которым ещё не записано наступление. Записать — дело хранилища.
 *
 * Решение принимается по сегодняшнему расписанию один раз и дальше не
 * пересматривается: перенос зарплатного дня не должен переписывать, какую
 * зарплату закрыли деньги прошлого месяца.
 */
export function pendingOccurrences(
  input: PeriodInput,
): { txId: string; occurrence: PlainDate; basis: OccurrenceBasis }[] {
  const ids = salarySources(input);
  const out: { txId: string; occurrence: PlainDate; basis: OccurrenceBasis }[] = [];
  for (const f of input.incomes) {
    if (f.occurrence || f.kind !== 'regular' || !f.streamId || !ids.has(f.streamId)) continue;
    const a = occurrenceOf(input.streams.find((s) => s.id === f.streamId), f);
    if (a) out.push({ txId: f.txId, ...a });
  }
  return out;
}

/**
 * Доход самого периода — та зарплата или выплата себе, что его открыла.
 *
 * Не даты расписания внутри отрезка: зарплата за 5 октября, пришедшая 8-го,
 * открыла период с 8-го, и наступления 5 октября в нём уже нет — а деньги
 * его. Сумма — сколько пришло по этой зарплате, но не меньше ожидаемого:
 * пришла часть — полученное плюс ожидаемый остаток, а не факт сверх полной
 * суммы; пришло больше — пришедшее.
 *
 * Что ждать дальше, говорит **последний по времени** ответ человека — где бы
 * он ни был записан:
 *
 * · при записи части назван остаток (`expectedRest`, «не жду» — ноль):
 *   план — пришедшее к тому моменту плюс названный остаток;
 * · у ожидания отмечено, что пришло часть или целиком (`decidedAt`): план —
 *   сумма ожидания; «больше не будет» или «денег не было» — только
 *   пришедшее.
 *
 * Части и ответы упорядочены по времени записи (`recordedAt`, `decidedAt`),
 * а не по виду записи, дате прихода или идентификатору. Записанное в одно и
 * то же мгновение считается пришедшим одновременно; из ответов с равным
 * временем берётся тот, что обещает меньше денег: завышенный доход — это
 * сломанное обещание в конце месяца, и случайный порядок его не выбирает.
 * План не меньше всего пришедшего: следующая часть остаток съедает, а не
 * добавляется к нему. Ответов нет вовсе — сумма открытого ожидания, а нет и
 * его — источника. Доход выключен — только пришедшее.
 *
 * `null` — у периода своего дохода нет (календарный месяц): тогда доход
 * плана — наступления источников в сроке.
 */
export function ownIncomeOf(
  p: ReportPeriod, input: PeriodInput, expectations: readonly IncomeExpectation[] = [],
): Money | null {
  if (p.kind === 'stable') {
    const plan = (input.stablePlans ?? []).find((x) => x.id === p.sourceId);
    const paid = (input.stablePayouts ?? []).find((f) => f.id === p.payoutId);
    if (paid && paid.amount > ZERO) return paid.amount;
    return plan ? plan.payout : null;
  }
  if (p.kind !== 'salary' || !p.sourceId || !p.occurrence) return null;
  const stream = input.streams.find((s) => s.id === p.sourceId);
  return occurrenceIncome(stream, p.sourceId, p.occurrence, input.incomes, expectations).planned;
}

/**
 * Одно наступление дохода: сколько по нему ждать и сколько пришло.
 *
 * Правила — те же, что у дохода периода (`ownIncomeOf`): последний по
 * времени ответ человека, при равном времени — осторожный; не меньше
 * пришедшего; ответов нет — открытое ожидание, а нет и его — сумма
 * источника; доход выключен — только пришедшее. Приход относится к
 * наступлению по своей связи (`occurrenceOf`), а не по датам окна: деньги
 * за 1 октября, пришедшие 30 сентября, — октябрьские.
 */
export function occurrenceIncome(
  stream: IncomeStream | undefined, streamId: string, occurrence: PlainDate,
  incomes: readonly PeriodIncome[], expectations: readonly IncomeExpectation[] = [],
): { planned: Money; received: Money } {
  const parts = incomes
    .filter((f) => f.kind === 'regular' && f.streamId === streamId && f.amount > 0)
    .filter((f) => occurrenceOf(stream, f)?.occurrence === occurrence)
    .sort((a, b) => whenOf(a).localeCompare(whenOf(b)) || a.txId.localeCompare(b.txId));
  const received = add(...parts.map((f) => f.amount));
  const atLeast = (m: Money): Money => (received > m ? received : m);
  const answer = (planned: Money) => ({ planned: atLeast(planned), received });
  // Доход выключен — от него больше ничего не ждут: только пришедшее.
  if (stream && !stream.isActive) return { planned: received, received };

  /*
    Ответы человека об остатке — по времени, последний побеждает.

    Ожидание без отметки времени решением не считается, если по нему ничего
    не отмечали: его завело приложение. Отмеченное до появления поля — самое
    раннее решение: любой ответ в операции позже него.
  */
  type Decision = { at: string; planned: Money };
  const decisions: Decision[] = [];
  for (const f of parts) {
    if (f.expectedRest === undefined) continue;
    const at = whenOf(f);
    // Пришедшее к этому ответу — всё записанное не позже него, включая
    // записанное в то же мгновение.
    const upTo = add(...parts.filter((x) => whenOf(x) <= at).map((x) => x.amount));
    decisions.push({ at, planned: add(upTo, f.expectedRest) });
  }
  const e = expectations.find((x) => x.streamId === streamId && x.expectedDate === occurrence);
  const closed = e && (e.status === 'received' || e.status === 'missed' || e.status === 'cancelled');
  if (e && (e.decidedAt || e.status !== 'planned')) {
    decisions.push({ at: e.decidedAt ?? '', planned: closed ? received : e.amount });
  }
  const last = decisions.reduce<Decision | null>((acc, x) => {
    if (!acc || x.at > acc.at) return x;
    // Равное время — осторожный ответ, а не случайный.
    return x.at === acc.at && x.planned < acc.planned ? x : acc;
  }, null);
  if (last) return answer(last.planned);
  if (e && !closed) return answer(e.amount);
  return answer(stream ? conservativeIncome(stream) : received);
}

/**
 * Когда записан приход — для порядка ответов. Без времени записи (данные
 * извне) порядком служит дата прихода: это всё, что о хронологии известно.
 */
const whenOf = (f: PeriodIncome): string => f.recordedAt ?? f.date;

/** Как приход разнесён по зарплатам — для уточнения спорных. */
export interface IncomeAssignment {
  txId: string;
  streamId: string;
  occurrence: PlainDate;
  basis: OccurrenceBasis;
  /** Этот приход открыл период своей зарплаты. */
  opens: boolean;
}

/**
 * Приходы источников периодов и то, к какой зарплате они отнесены.
 *
 * Отсюда интерфейс узнаёт спорную операцию: досрочная ноябрьская зарплата
 * без связи с ожиданием отнесена к октябрю как доплата, и спросить надо
 * именно про неё, а не про период целиком.
 */
export function incomeAssignments(input: PeriodInput): IncomeAssignment[] {
  const ids = salarySources(input);
  const facts = input.incomes
    .filter((f) => f.kind === 'regular' && f.streamId && ids.has(f.streamId) && f.amount > 0)
    .sort((a, b) => a.date.localeCompare(b.date) || a.txId.localeCompare(b.txId));
  const seen = new Set<string>();
  const out: IncomeAssignment[] = [];
  for (const f of facts) {
    const a = occurrenceOf(input.streams.find((s) => s.id === f.streamId), f);
    if (!a) continue;
    const key = `${f.streamId}:${a.occurrence}`;
    out.push({ txId: f.txId, streamId: f.streamId!, ...a, opens: !seen.has(key) });
    seen.add(key);
  }
  return out;
}

// ───────────────────────────────────────────────────────────── опоры

/** Начало периода с тем, что нужно для его конца. */
interface Anchor {
  key: string;
  occurrence?: PlainDate;
  payoutId?: string;
  from: PlainDate;
  start: ReportPeriod['start'];
  certain: boolean;
  source: PeriodSource;
  /** Начало следующего периода по расписанию; `null` — даты нет. */
  nextStart: PlainDate | null;
  rank: number;
  why: string;
  /** Когда записан открывший приход (`recordedAt`). */
  recordedAt?: string;
  /** Отрезок истории (номер закрепления); `-1` — календарь до первой опоры. */
  seg?: number;
}

const firstOfMonth = (d: PlainDate): PlainDate => {
  const { year, month } = parts(d);
  return ymd(year, month, 1);
};
const firstOfNextMonth = (d: PlainDate): PlainDate => {
  const { year, month } = parts(d);
  return month === 12 ? ymd(year + 1, 1, 1) : ymd(year, month + 1, 1);
};

/** Календарные месяцы: месяц `from` и все, чьи первые числа не позже `to`. */
function monthAnchors(from: PlainDate, to: PlainDate, why: string): Anchor[] {
  const out: Anchor[] = [];
  for (let d = firstOfMonth(from); !isAfter(d, to); d = firstOfNextMonth(d)) {
    out.push({
      key: `month:${d.slice(0, 7)}`,
      from: d,
      start: 'calendar',
      certain: true,
      source: { kind: 'month' },
      nextStart: firstOfNextMonth(d),
      rank: 3,
      why,
    });
  }
  return out;
}

function salaryAnchors(sourceId: string, input: PeriodInput, first: PeriodPin | null): Anchor[] {
  const stream = input.streams.find((s) => s.id === sourceId);
  const live = stream ? { ...stream, isActive: true } : undefined;
  const source: PeriodSource = { kind: 'salary', sourceId };
  const name = stream?.name ?? 'Доход';
  const next = (occ: PlainDate) => (live ? nextByRhythm(rhythmOf(live), occ) : null);

  /*
    Части одной зарплаты — одно наступление.

    Первая положительная часть открывает период, остальные только доливают
    деньги. Иначе зарплата, пришедшая двумя переводами, дала бы период в
    один день.
  */
  const groups = new Map<PlainDate, { opener: PeriodIncome; certain: boolean }>();
  const facts = input.incomes
    .filter((f) => f.kind === 'regular' && f.streamId === sourceId && f.amount > 0
      && !isAfter(f.date, input.today))
    .sort((a, b) => a.date.localeCompare(b.date) || a.txId.localeCompare(b.txId));
  for (const f of facts) {
    const a = occurrenceOf(stream, f);
    if (!a) continue;
    if (!groups.has(a.occurrence)) groups.set(a.occurrence, { opener: f, certain: a.basis !== 'schedule' });
  }

  const out: Anchor[] = [...groups.entries()].map(([occ, g]) => ({
    key: `occ:${sourceId}:${occ}`,
    occurrence: occ,
    from: g.opener.date,
    start: 'fact',
    certain: g.certain,
    source,
    nextStart: next(occ),
    rank: 0,
    why: `${name}: от прихода до прихода`,
    ...(g.opener.recordedAt ? { recordedAt: g.opener.recordedAt } : {}),
  }));

  if (first) {
    /*
      Расписание — только там, где за приход приложение не отвечает.

      До начала учёта приход не спрашивается: человек назвал остатки уже с
      ним. После начала учёта нужен факт: незаписанная зарплата значит «ещё
      не пришла», и период тянется. Начало, однажды закреплённое, берётся
      из закрепления — перенос зарплатного дня его не двигает.
    */
    const since = input.since ?? input.today;
    const occ = first.startedFrom
      ?? (live ? previousOccurrence(live, addDays(since, -1)) : null);
    if (occ && !groups.has(occ)) {
      out.push({
        key: `occ:${sourceId}:${occ}`,
        occurrence: occ,
        from: occ,
        start: 'schedule',
        certain: false,
        source,
        nextStart: next(occ),
        rank: 2,
        why: `${name}: по расписанию — приход до начала учёта`,
      });
    }
  }
  return out;
}

function stableAnchors(planId: string, input: PeriodInput): Anchor[] {
  const plan = (input.stablePlans ?? []).find((p) => p.id === planId);
  const source: PeriodSource = { kind: 'stable', sourceId: planId };
  /*
    Период открывает только выплата.

    Пропуск, сверка и изъятие из запаса вне графика деньгами-зарплатой не
    являются: пропуск — решение не брать, сверка — поправка остатка задним
    числом, изъятие — деньги раньше срока, и записывается оно движением
    буфера, а не выплатой. Ни одно из них не начинает новый период.
  */
  const paid = (input.stablePayouts ?? [])
    .filter((f) => f.planId === planId && decisionOf(f) === 'paid' && f.amount > 0)
    .filter((f) => !isAfter(paidOnOf(f), input.today));
  // Две выплаты одним днём (догнал просроченную) — один период.
  const byDay = new Map<PlainDate, StablePayoutFact>();
  for (const f of paid) {
    const day = paidOnOf(f);
    const was = byDay.get(day);
    if (!was || isAfter(f.scheduledDate, was.scheduledDate)) byDay.set(day, f);
  }
  return [...byDay.entries()].map(([day, f]) => ({
    key: `payout:${f.id}`,
    payoutId: f.id,
    from: day,
    start: 'fact',
    certain: true,
    source,
    nextStart: !plan || isPaused(plan)
      ? null
      : payoutOccurrences(plan, addDays(f.scheduledDate, 1), addDays(f.scheduledDate, 70))[0] ?? null,
    rank: 1,
    why: 'От выплаты себе до выплаты',
  }));
}

/** Опоры одного источника. Первому отрезку истории можно опереться на расписание. */
function anchorsOf(
  pin: PeriodPin, input: PeriodInput, windowFrom: PlainDate, first: boolean, why: string,
): Anchor[] {
  if (pin.kind === 'salary' && pin.sourceId) return salaryAnchors(pin.sourceId, input, first ? pin : null);
  if (pin.kind === 'stable' && pin.sourceId) return stableAnchors(pin.sourceId, input);
  return monthAnchors(windowFrom, input.today, why);
}

// ─────────────────────────────────────────────────────────────── периоды

/**
 * Когда идущий период закончит смена источника.
 *
 * Новый источник закрепляется днём смены, а вступает с первого своего
 * прихода не раньше этого дня (`reportPeriods`): месяц — с первого числа,
 * зарплата — с прихода по её расписанию, выплата себе — с выплаты. Пока
 * закрепления нет (приложение запишет его при загрузке), смена считается
 * сегодняшней. Прежний источник, если он ещё действует, может закончить
 * период и раньше — своим приходом; тогда смена на конец не влияет.
 */
function switchOf(
  current: PeriodSource, from: PlainDate, plannedTo: PlainDate | null, input: PeriodInput,
  pins: readonly PeriodPin[], fresh: PeriodSource,
): { end: PlainDate | null; next: PeriodSource } | null {
  const last = pins[pins.length - 1];
  const incoming: PeriodPin | null = last && !sameSource(last, current) ? last
    : !sameSource(fresh, current) ? { ...sourceOf(fresh), since: input.today } : null;
  if (!incoming) return null;
  // Приход, который закончит этот период: не раньше дня смены и после его
  // начала. Наступление до начала периода — не его конец: оно давно прошло,
  // и ждать его бессмысленно.
  const after = isAfter(incoming.since, from) ? incoming.since : addDays(from, 1);
  let start: PlainDate | null = null;
  if (incoming.kind === 'month') {
    start = parts(after).day === 1 ? after : firstOfNextMonth(after);
  } else if (incoming.kind === 'salary') {
    const s = input.streams.find((x) => x.id === incoming.sourceId);
    start = s ? nextByRhythm(rhythmOf({ ...s, isActive: true }), addDays(after, -1)) : null;
  } else {
    const plan = (input.stablePlans ?? []).find((x) => x.id === incoming.sourceId);
    // На паузе или отключён — выплат не будет, и срок не выдумываем.
    start = plan && plan.status === 'active' && !isPaused(plan)
      ? payoutOccurrences(plan, after, addDays(after, 70))[0] ?? null : null;
  }
  // Действует ли ещё прежний источник — может ли он сам закрыть период.
  const goesOn = current.kind === 'month'
    || (current.kind === 'salary' && input.streams.some((x) => x.id === current.sourceId && x.isActive))
    || (current.kind === 'stable' && (() => {
      const plan = activeStablePlan(input.stablePlans ?? []);
      return !!plan && plan.id === current.sourceId && !isPaused(plan);
    })());
  // Дат у нового источника нет: срок неизвестен — так и говорим.
  if (!start) return goesOn ? null : { end: null, next: sourceOf(incoming) };
  const end = addDays(start, -1);
  if (goesOn && plannedTo && !isBefore(end, plannedTo)) return null;
  return { end, next: sourceOf(incoming) };
}

/**
 * Все периоды от начала истории до сегодняшнего, по возрастанию.
 *
 * Периоды идут встык: конец одного — день перед началом следующего. Поэтому
 * любая операция попадает ровно в один период, и после правки или удаления
 * операции периоды просто считаются заново.
 */
export function reportPeriods(input: PeriodInput): ReportPeriod[] {
  const windowFrom = input.from ?? input.since ?? addDays(input.today, -365);
  const fresh = mainSource(input);
  const pins: PeriodPin[] = input.pins && input.pins.length > 0
    ? [...input.pins].sort((a, b) => a.since.localeCompare(b.since))
    : [{ ...sourceOf(fresh), since: windowFrom }];
  const monthWhy = fresh.kind === 'month' ? fresh.why : 'Календарный месяц';

  /*
    Отрезки истории по закреплениям.

    Первый отрезок покрывает всё прошлое. Каждый следующий начинается с
    первого прихода своего источника не раньше дня закрепления: пока его
    нет, идёт период прежнего источника.

    Приход, внесённый после закрепления задним числом (первую зарплату
    новой работы записали назавтра), — тоже его приход, но не раньше начала
    периода, что шёл в день смены: старые поступления этого источника
    сменой не становятся.

    Источник, так и не успевший прийти до следующей смены, не вступает
    вовсе: промежуточный выбор («дохода нет» между выключенной зарплатой и
    новой работой, второй доход, добавленный и тут же выключенный) историю
    не режет.
  */
  type Segment = { start: PlainDate | null; anchors: Anchor[] };
  const segments: Segment[] = [];
  // Начало смены: первое закрепление после вступившего отрезка. Смена
  // работы в два шага («завёл новую» → «выключил старую») начинается с
  // первого: приход новой работы между ними — уже её.
  let change: PeriodPin | null = null;
  pins.forEach((pin, i) => {
    const all = anchorsOf(pin, input, windowFrom, i === 0, monthWhy).map((a) => ({ ...a, seg: i }));
    if (i === 0) {
      segments.push({ start: null, anchors: all });
      return;
    }
    const from = change ?? pin;
    const prev = segments[segments.length - 1]!;
    // Начало периода, что шёл, когда смена началась.
    const running = prev.anchors
      .filter((a) => !isAfter(a.from, from.since) && (!prev.start || !isBefore(a.from, prev.start)))
      .reduce<PlainDate | null>((m, a) => (!m || isAfter(a.from, m) ? a.from : m), prev.start);
    const recordedAfter = (a: Anchor): boolean => !!a.recordedAt
      && (from.at ? a.recordedAt >= from.at : a.recordedAt.slice(0, 10) >= from.since);
    const enters = (a: Anchor): boolean => (!prev.start || isAfter(a.from, prev.start))
      && (!isBefore(a.from, from.since) || (recordedAfter(a) && !!running && isAfter(a.from, running)));
    const first = all.filter(enters).sort((a, b) => a.from.localeCompare(b.from))[0];
    const nextPin = pins[i + 1];
    if (first && (!nextPin || isBefore(first.from, nextPin.since))) {
      segments.push({ start: first.from, anchors: all });
      change = null;
    } else if (!change) {
      change = pin;
    }
  });

  const anchors: Anchor[] = [];
  segments.forEach((seg, i) => {
    const end = segments[i + 1]?.start ?? null;
    for (const a of seg.anchors) {
      if (seg.start && isBefore(a.from, seg.start)) continue;
      if (end && !isBefore(a.from, end)) continue;
      anchors.push(a);
    }
  });

  const sorted = anchors
    .filter((a) => !isAfter(a.from, input.today))
    .sort((a, b) => a.from.localeCompare(b.from) || a.rank - b.rank);
  /*
    Начало истории без опоры — календарём.

    Стабильный бюджет до первой выплаты, источник без единого прихода:
    отрезку нужно начало, и выдумывать его по расписанию нельзя.
  */
  const firstFrom = sorted[0]?.from ?? null;
  if (!firstFrom || isAfter(firstFrom, windowFrom)) {
    sorted.unshift(...monthAnchors(
      windowFrom,
      firstFrom ? addDays(firstFrom, -1) : input.today,
      fresh.kind === 'month' ? fresh.why : 'Календарный месяц: приходов главного дохода ещё нет',
    ).map((a) => ({ ...a, seg: -1 })));
  }

  // Две опоры одним днём — один период: первой по надёжности.
  const unique: Anchor[] = [];
  for (const a of sorted) {
    if (unique.length > 0 && unique[unique.length - 1]!.from === a.from) continue;
    unique.push(a);
  }

  const periods = unique.map((a, i): ReportPeriod => {
    const next = unique[i + 1];
    const to = next ? addDays(next.from, -1) : null;
    const plannedTo = a.nextStart ? addDays(a.nextStart, -1) : null;
    const change = to === null ? switchOf(a.source, a.from, plannedTo, input, pins, fresh) : null;
    // Следующий период — из другого отрезка и другого источника: этот
    // оборвала смена. Добавил второй доход и сразу выключил — источник тот
    // же, период кончился своей зарплатой.
    const cut = !!next && (a.seg ?? 0) >= 0 && next.seg !== a.seg && !sameSource(next.source, a.source);
    const expectedTo = to ?? plannedTo;
    const observedFrom = input.since && isAfter(input.since, a.from) ? input.since : a.from;
    return {
      key: a.key,
      kind: a.source.kind,
      ...(a.source.sourceId ? { sourceId: a.source.sourceId } : {}),
      from: a.from,
      start: a.start,
      certain: a.certain,
      to,
      expectedTo,
      plannedTo,
      ...(a.occurrence ? { occurrence: a.occurrence } : {}),
      ...(a.payoutId ? { payoutId: a.payoutId } : {}),
      observedFrom,
      partial: observedFrom !== a.from,
      // Срок вышел — только когда прошёл сам день прихода: утром в день
      // зарплаты она ещё не опоздала.
      overdue: to === null && expectedTo !== null && isAfter(input.today, addDays(expectedTo, 1)),
      ...(change ? { next: change.next, ...(change.end ? { switchEnd: change.end } : {}) } : {}),
      ...(cut ? { endedBySwitch: true as const } : {}),
      why: a.why,
    };
  });

  return periods.filter((p) => p.to === null || !isBefore(p.to, windowFrom));
}

/** Идущий период. История пуста не бывает: в худшем случае это месяц. */
export const currentPeriod = (input: PeriodInput): ReportPeriod => {
  const all = reportPeriods(input);
  return all[all.length - 1]!;
};

/** В какой период попадает день. */
export const periodAt = (
  periods: readonly ReportPeriod[], date: PlainDate,
): ReportPeriod | null => periods.find(
  (p) => !isBefore(date, p.from) && (p.to === null || !isAfter(date, p.to)),
) ?? null;

/**
 * Последний день срока плана — по расписанию.
 *
 * Не фактическое закрытие: зарплата с опозданием закрыла период позже, но
 * плановый срок от этого не удлиняется, и в него не попадает следующая
 * зарплата. Даты по расписанию нет (пауза стабильного бюджета) — закрытие
 * или конец месяца.
 */
export const periodEnd = (p: ReportPeriod): PlainDate => {
  // Смена источника закончит идущий период по своему прогнозу: план не
  // берёт доходы, которые достанутся следующему.
  if (p.to === null && p.switchEnd) return p.switchEnd;
  // Период оборвала смена — срок плана не дальше его конца.
  if (p.to && p.endedBySwitch && p.plannedTo && isBefore(p.to, p.plannedTo)) return p.to;
  if (p.plannedTo) return p.plannedTo;
  if (p.to) return p.to;
  const { year, month } = parts(p.from);
  return ymd(year, month, daysInMonth(year, month));
};

/**
 * Какие операции относятся к периоду.
 *
 * Не то же самое, что срок плана. Зарплата задержалась — период идёт после
 * ожидаемого конца, и трата 7 ноября принадлежит ему, хотя план закончился
 * 4-го. Поэтому у идущего периода факты берутся по сегодняшний день, а срок
 * плана остаётся прежним и от задержки не растёт.
 */
export const factsWindowOf = (
  p: ReportPeriod, today: PlainDate,
): { from: PlainDate; to: PlainDate } => ({ from: p.from, to: p.to ?? today });

const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Срок плана периода для `computeBudget`. Факты — `factsWindowOf`. */
export const planPeriodOf = (p: ReportPeriod): PlanPeriod => {
  const { month, day } = parts(p.from);
  return { from: p.from, to: periodEnd(p), label: `с ${day} ${MONTHS_GEN[month - 1]}` };
};
